/**
 * useHaptic Hook
 *
 * Provides haptic feedback patterns for mobile devices.
 * Supports light, medium, heavy, success, and error patterns.
 * Automatically detects browser support and respects reduced motion preference.
 *
 * @example
 * ```tsx
 * const haptic = useHaptic();
 *
 * <button onClick={() => {
 *   haptic.light();
 *   handleClick();
 * }}>
 *   Tap me
 * </button>
 * ```
 */

export type HapticPattern = 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'warning' | 'selection';

export interface HapticAPI {
  /** Light tap feedback (10ms) - for button presses */
  light: () => void;
  /** Medium feedback (20ms) - for selections */
  medium: () => void;
  /** Heavy feedback (50ms) - for major actions */
  heavy: () => void;
  /** Success pattern - double tap for confirmations */
  success: () => void;
  /** Error pattern - warning vibration for failures */
  error: () => void;
  /** Warning pattern - single long pulse */
  warning: () => void;
  /** Selection changed feedback */
  selection: () => void;
  /** Custom vibration pattern */
  vibrate: (pattern: number | number[]) => void;
  /** Execute haptic by pattern name */
  trigger: (pattern: HapticPattern) => void;
  /** Whether vibration is supported */
  isSupported: boolean;
  /** Whether haptics are enabled (support + no reduced motion) */
  isEnabled: boolean;
}

/**
 * Check if user prefers reduced motion
 */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useHaptic(): HapticAPI {
  const isSupported = typeof navigator !== 'undefined' && 'vibrate' in navigator;
  const isEnabled = isSupported && !prefersReducedMotion();

  const vibrate = (pattern: number | number[]) => {
    // Respect reduced motion preference
    if (isEnabled) {
      try {
        navigator.vibrate(pattern);
      } catch {
        // Silently fail if vibration fails
      }
    }
  };

  const patterns: Record<HapticPattern, () => void> = {
    // Light tap - 10ms - for button presses
    light: () => vibrate(10),

    // Medium feedback - 20ms - for selections
    medium: () => vibrate(20),

    // Heavy feedback - 50ms - for major actions
    heavy: () => vibrate(50),

    // Success pattern - double tap
    success: () => vibrate([10, 50, 10]),

    // Error pattern - warning vibration
    error: () => vibrate([50, 100, 50]),

    // Warning pattern - single long pulse
    warning: () => vibrate([100]),

    // Selection changed - very light
    selection: () => vibrate(5),
  };

  const trigger = (pattern: HapticPattern) => {
    patterns[pattern]?.();
  };

  return {
    ...patterns,
    vibrate,
    trigger,
    isSupported,
    isEnabled,
  };
}
